import { create } from 'zustand';

export type Platform = 'win32' | 'darwin' | 'linux' | 'unknown';
export type DeploymentMode = 'desktop' | 'web' | null;

interface PlatformStore {
  // State
  platform: Platform;
  isInitialized: boolean;
  deploymentMode: DeploymentMode;
  isBackendReady: boolean;
  isConnectingToBackend: boolean;

  // Computed getters (derived from state)
  isDesktop: () => boolean;
  baseURL: () => string;

  // Actions
  initializePlatform: () => Promise<void>;
  fetchDeploymentMode: () => Promise<void>;
  waitForBackend: () => void;
}

// Helper function to validate platform strings
const isValidPlatform = (value: string): value is Platform => {
  return ['darwin', 'win32', 'linux', 'unknown'].includes(value);
};

/**
 * Detects if running in Electron desktop mode
 * Safe to call in both desktop and web environments
 * Checks for window.electron existence without calling any methods
 */
const checkIsDesktop = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Electron renderer is served via app:// (or legacy file://) — both signal desktop
  const proto = window.location.protocol;
  if (proto === 'app:' || proto === 'file:') {
    return true;
  }

  // Type-safe check for window.electron
  const win = window as Window & { electron?: any };
  return typeof win.electron !== 'undefined';
};


let backendRetryTimer: ReturnType<typeof setTimeout> | null = null;
let backendRetryCount = 0;

export const usePlatformStore = create<PlatformStore>((set, get) => ({
  platform: 'unknown',
  isInitialized: false,
  deploymentMode: null,
  isBackendReady: false,
  isConnectingToBackend: false,

  // Computed getters
  isDesktop: () => checkIsDesktop(),
  
  baseURL: () => {
    // Electron renderer protocols (app:// preferred, file:// legacy) — backend is local.
    // Also covers the case where the preload script hasn't loaded yet.
    if (typeof window !== 'undefined') {
      const proto = window.location.protocol;
      if (proto === 'app:' || proto === 'file:') {
        return 'http://127.0.0.1:8000';
      }
    }

    // Check if we're in desktop mode - safe check that won't error in web
    if (checkIsDesktop()) {
      // Desktop mode: backend runs on localhost:8000
      return 'http://127.0.0.1:8000';
    }

    // Web mode: check for configured backend URL
    // Check window.__BACKEND_URL__ (can be set by index.html or runtime config)
    const win = window as Window & { __BACKEND_URL__?: string };
    if (typeof window !== 'undefined' && typeof win.__BACKEND_URL__ === 'string') {
      // If explicitly set to empty string, use relative URLs (for dev proxy)
      if (win.__BACKEND_URL__ === '') {
        return '';
      }
      // Otherwise use the configured URL
      return win.__BACKEND_URL__;
    }

    // Fallback: use empty string for relative URLs (works with Vite proxy in dev)
    // In production, this allows same-origin requests
    return '';
  },
  
  initializePlatform: async () => {
    // Prevent multiple initializations
    if (get().isInitialized) {
      return;
    }
    
    // Check isDesktop FIRST before attempting any Electron API calls
    const isDesktop = checkIsDesktop();
    
    // If not desktop, platform is unknown (OS detection only needed for desktop)
    if (!isDesktop) {
      set({ platform: 'unknown', isInitialized: true });
      return;
    }
    
    // Desktop mode: try to get platform from Electron API
    // Only call window.electron.getPlatform if we confirmed isDesktop is true
    const win = window as Window & { electron?: { getPlatform?: () => Promise<string> } };
    if (typeof window === 'undefined' || !win.electron?.getPlatform) {
      // If Electron API not available, set to unknown
      set({ platform: 'unknown', isInitialized: true });
      return;
    }
    
    try {
      const p = await win.electron.getPlatform();
      if (isValidPlatform(p)) {
        set({ platform: p, isInitialized: true });
      } else {
        set({ platform: 'unknown', isInitialized: true });
      }
    } catch (error) {
      console.error("Error fetching device's os platform:", error);
      set({ platform: 'unknown', isInitialized: true });
    }
  },

  fetchDeploymentMode: async () => {
    // Don't re-fetch if already resolved
    if (get().deploymentMode !== null) {
      return;
    }

    try {
      const baseURL = get().baseURL();
      const response = await fetch(`${baseURL}/deployment`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // Endpoint missing or errored — don't assume a mode.
        // Leave as null so the auth gate stays in 'loading' until
        // the backend is reachable and returns a definitive answer.
        return;
      }

      const data = await response.json();
      const mode = data.deployment_mode;
      set({ deploymentMode: mode === 'desktop' ? 'desktop' : 'web' });
    } catch {
      // Network error — backend not reachable yet. Leave as null.
    }
  },

  waitForBackend: () => {
    const { isBackendReady, isConnectingToBackend } = get();
    if (isBackendReady || isConnectingToBackend) return;

    set({ isConnectingToBackend: true });
    backendRetryCount = 0;

    const attempt = async () => {
      try {
        const baseURL = get().baseURL();
        const response = await fetch(`${baseURL}/health`, {
          credentials: 'include',
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // Backend is ready
        set({ isBackendReady: true, isConnectingToBackend: false });
        backendRetryCount = 0;
        if (backendRetryTimer) {
          clearTimeout(backendRetryTimer);
          backendRetryTimer = null;
        }

        // Resolve deployment mode now that backend is reachable
        get().fetchDeploymentMode();
      } catch {
        backendRetryCount += 1;
        if (backendRetryCount === 1) {
          console.warn('Backend not available yet, retrying...');
        }

        backendRetryTimer = setTimeout(attempt, 3000);
      }
    };

    attempt();
  },
}));

